import React from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';

/**
 * UniversalLanguageField - Single field for any language input (BM/EN/Mix)
 * 
 * Management can type naturally in any language:
 * - Pure BM: "Kopi herba untuk kawal gula darah"
 * - Pure EN: "Herbal coffee to control blood sugar"
 * - Code-switching: "Kopi herba ni special untuk control sugar level"
 * 
 * AI handles language detection and translation at runtime (generation time)
 * 
 * Usage:
 * <UniversalLanguageField
 *   label="Product Name"
 *   value="Kopi Ala Kazim untuk Diabetes"
 *   onChange={(value) => {}}
 *   helper="Enter your product name in any language"
 *   required={true}
 *   multiline={false}
 *   placeholder="Type in BM, EN, or mix..."
 * />
 */
const UniversalLanguageField = ({
  label,
  value = '',
  onChange,
  helper = '',
  required = false,
  multiline = false,
  rows = 3,
  placeholder = 'Type in any language (BM, EN, or mix)...',
  onBlur,
  hasError = false,
  errorMessage = '',
  language = 'urban', // Current UI language
  adaptMessage = 'AI will adapt to audience language at generation time',
}) => {
  const InputComponent = multiline ? 'textarea' : 'input';

  return (
    <div style={{ marginBottom: EMZI_SPACING.md }}>
      {/* Label */}
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        marginBottom: EMZI_SPACING.xs,
        color: EMZI_COLORS.textPrimary,
      }}>
        {label}
        {required && <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>}
      </label>

      {/* Helper text */}
      {helper && (
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
          lineHeight: '1.6'
        }}>
          💡 {helper}
        </div>
      )}

      {/* Universal Input Field */}
      <InputComponent
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        rows={multiline ? rows : undefined}
        style={{
          width: '100%',
          padding: EMZI_SPACING.sm,
          border: `2px solid ${hasError ? EMZI_COLORS.error : EMZI_COLORS.border}`,
          borderRadius: EMZI_RADIUS.md,
          fontSize: '16px',
          fontFamily: 'inherit',
          outline: 'none',
          resize: multiline ? 'vertical' : 'none',
          transition: 'border-color 0.2s',
          backgroundColor: '#fff',
        }}
        onFocus={(e) => {
          if (!hasError) {
            e.target.style.borderColor = EMZI_COLORS.primary;
          }
        }}
        onBlur={(e) => {
          e.target.style.borderColor = hasError ? EMZI_COLORS.error : EMZI_COLORS.border;
          if (onBlur) onBlur();
        }}
      />

      {/* Error Message */}
      {hasError && errorMessage && (
        <div style={{
          marginTop: '6px',
          fontSize: '12px',
          color: EMZI_COLORS.error,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <span>⚠️</span>
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Language flexibility notice */}
      {!hasError && value && value.length > 5 && (
        <div style={{
          marginTop: '6px',
          fontSize: '11px',
          color: EMZI_COLORS.success,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <span>✓</span>
          <span>{adaptMessage}</span>
        </div>
      )}
    </div>
  );
};

export default UniversalLanguageField;
